import { Bell, Building, CheckSquare, Menu, Plus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useNotificationCount } from '@/features/comunicacao/hooks/useNotificationCount';
import QuickTaskCreator from '@/features/tarefas/components/QuickTaskCreator';

import MobileSidebarDrawer from './MobileSidebarDrawer';

export default function MobileTabBar() {
  const location = useLocation();
  const [showQuickTask, setShowQuickTask] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const barRef = useRef<HTMLDivElement | null>(null);
  const { mentions, total } = useNotificationCount();
  const mentionCount = mentions;
  const otherCount = Math.max(0, total - mentionCount);

  const navigation = [
    { name: 'Obras', href: '/obras', icon: Building },
    { name: 'Tarefas', href: '/minhas-tarefas', icon: CheckSquare },
    { name: 'Notificações', href: '/notificacoes', icon: Bell }
  ];

  const isActive = (href: string) => {
    return location.pathname.startsWith(href);
  };

  const handleQuickTaskSuccess = () => {
    // Callback quando tarefa for criada com sucesso
    // Pode adicionar notificação ou reload se necessário
  };

  // (sem portal) — permanece inline no fluxo do mesmo scroll container

  // Publica a altura da TabBar como CSS var p/ quem precisa reservar espaço
  useEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const set = () => {
      const h = el.offsetHeight || 64;
      document.body.style.setProperty('--tabbar-bottom-offset', `${h}px`);
    };
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    window.addEventListener('resize', set);
    window.addEventListener('orientationchange', set);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', set);
      window.removeEventListener('orientationchange', set);
      document.body.style.removeProperty('--tabbar-bottom-offset');
    };
  }, []);

  // (sticky dispensa visualViewport)


  const Bar = (
    <div
      ref={barRef}
      className="sticky bottom-0 left-0 right-0 bg-background dark:bg-gray-900 border-t border-secondary dark:border-gray-700 z-40 md:hidden"
      // 🌟 iOS fix: força layer própria para o Safari não "levar" a barra no scroll/drag
      style={{
        WebkitTransform: 'translateZ(0)',
        transform: 'translateZ(0)',
        WebkitBackfaceVisibility: 'hidden',
        backfaceVisibility: 'hidden',
        // notch / home indicator
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        // ajuda o compositor do Safari a manter o elemento fixo ao viewport visível
        willChange: 'transform'
      }}
      >
          <nav className="flex items-center justify-around px-2 py-2">
            {/* Primeiro item -> MENU (abre o Drawer/Sidebar) */}
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              aria-label="Abrir menu"
              className={`flex flex-col items-center justify-center p-2 rounded-lg min-w-0 flex-1 transition-colors ${
                drawerOpen
                  ? 'text-primary dark:text-blue-400 bg-primary/10 dark:bg-blue-900/20'
                  : 'text-gray-600 dark:text-gray-400 hover:text-foreground dark:hover:text-gray-100'
              }`}
            >
              <Menu className="w-5 h-5 mb-1" />
              <span className="text-xs font-ageo-regular truncate w-full text-center">Menu</span>
            </button>

            {/* Segundo item */}
            <Link
              to={navigation[1].href}
              className={`flex flex-col items-center justify-center p-2 rounded-lg min-w-0 flex-1 transition-colors ${
                isActive(navigation[1].href)
                  ? 'text-primary dark:text-blue-400 bg-primary/10 dark:bg-blue-900/20'
                  : 'text-gray-600 dark:text-gray-400 hover:text-foreground dark:hover:text-gray-100'
              }`}
            >
              <div className="relative">
                {(() => {
                  const Icon = navigation[1].icon;
                  return <Icon className="w-5 h-5 mb-1" />;
                })()}
              </div>
              <span className="text-xs font-ageo-regular truncate w-full text-center">
                {navigation[1].name}
              </span>
            </Link>

            {/* Botão + Nova Tarefa - CENTRO DESTACADO */}
            <button
              onClick={() => setShowQuickTask(true)}
              className="flex flex-col items-center justify-center p-3 rounded-xl bg-confirmation dark:bg-green-600 text-white shadow-lg transform transition-all hover:scale-105 active:scale-95"
            >
              <Plus className="w-6 h-6 mb-1" />
              <span className="text-xs font-ageo-bold">
                Nova
              </span>
            </button>

            {/* Terceiro item - Notificações */}
            <Link
              to={navigation[2].href}
              className={`flex flex-col items-center justify-center p-2 rounded-lg min-w-0 flex-1 transition-colors ${
                isActive(navigation[2].href)
                  ? 'text-primary dark:text-blue-400 bg-primary/10 dark:bg-blue-900/20'
                  : 'text-gray-600 dark:text-gray-400 hover:text-foreground dark:hover:text-gray-100'
              }`}
            >
              <div className="relative">
                {(() => {
                  const Icon = navigation[2].icon;
                  return <Icon className="w-5 h-5 mb-1" />;
                })()}
                {/* 🔴 Menções (vermelho) */}
                {mentionCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[16px] h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                    {mentionCount > 99 ? '99+' : mentionCount}
                  </span>
                )}
                {/* 🔵 Outras (azul) */}
                {otherCount > 0 && (
                  <span className="absolute -bottom-1 -right-1 min-w-[14px] h-[14px] bg-blue-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                    {otherCount > 99 ? '99+' : otherCount}
                  </span>
                )}
              </div>
              <span className="text-xs font-ageo-regular truncate w-full text-center">
                {navigation[2].name}
              </span>
            </Link>

            {/* Quarto item -> OBRAS (foi para a direita) */}
            <Link
              to={navigation[0].href}
              className={`flex flex-col items-center justify-center p-2 rounded-lg min-w-0 flex-1 transition-colors ${
                isActive(navigation[0].href)
                  ? 'text-primary dark:text-blue-400 bg-primary/10 dark:bg-blue-900/20'
                  : 'text-gray-600 dark:text-gray-400 hover:text-foreground dark:hover:text-gray-100'
              }`}
            >
              <div className="relative">
                {(() => {
                  const Icon = navigation[0].icon;
                  return <Icon className="w-5 h-5 mb-1" />;
                })()}
              </div>
              <span className="text-xs font-ageo-regular truncate w-full text-center">
                {navigation[0].name}
              </span>
            </Link>

          </nav>
      </div>
  );

  return (
    <>
      {/* Drawer de navegação (SideBar no mobile) */}
      <MobileSidebarDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
      {/* Agora a barra participa do fluxo (sticky) dentro do mesmo scroll container */}
      {Bar}

      {/* Quick Task Creator Modal */}
      <QuickTaskCreator
        isOpen={showQuickTask}
        onClose={() => setShowQuickTask(false)}
        onSuccess={handleQuickTaskSuccess}
      />
    </>
  );
}